<div
  class="flight-card"
  [ngClass]="policyClass"
  [class.is-expanded]="isExpanded"
  role="button"
  [attr.tabindex]="isBlocked ? -1 : 0"
  [attr.aria-disabled]="isBlocked ? true : null"
  [attr.aria-expanded]="hasFareOptions ? isExpanded : null"
  [attr.aria-label]="
    fareOptionsLabel +
    ' ' +
    (card.airline ? card.airline + ' ' : '') +
    card.outbound.fromCode +
    ' to ' +
    card.outbound.toCode
  "
  (click)="onClick()"
  (keydown.enter)="onKey($event)"
  (keydown.space)="onKey($event)"
>
  @if (policyLabel) {
    <span class="visuallyhidden">{{ policyLabel }}</span>
  }
  <div class="flight-card__body">
    <div class="flight-card__main">
      <!-- Outbound leg -->
      <div class="flight-card__leg">
        <div class="flight-card__leg-head">
          @if (airlineLogoUrl) {
            <img
              class="flight-card__airline-logo"
              [src]="airlineLogoUrl"
              [alt]="card.airline"
              (error)="$any($event.target).style.display = 'none'"
            />
          }
          <span class="flight-card__date">{{ card.outbound.departDate }}</span>
          @if (card.airline) {
            <span class="flight-card__airline">{{ card.airline }}</span>
          }
        </div>
        <button
          type="button"
          class="flight-card__times"
          [disabled]="isBlocked"
          (click)="onSelectFare($event)"
          i18n-aria-label="@@chatFlightSelectTimesAria"
          aria-label="View this flight on the results page"
        >
          <span class="flight-card__time">{{ card.outbound.departTime }}</span>
          <span class="flight-card__arrow">→</span>
          <span class="flight-card__time">{{ card.outbound.arriveTime }}</span>
        </button>
        <div class="flight-card__route">
          {{ card.outbound.fromCode }} → {{ card.outbound.toCode }}
        </div>
        <div class="flight-card__meta">
          @if (card.outbound.duration) {
            <span class="flight-card__duration">{{ card.outbound.duration }}</span>
            <span class="flight-card__separator">·</span>
          }
          <span class="flight-card__stops">{{ stopsLabel(card.outbound.stops) }}</span>
          @if (card.outbound.flightNumbers) {
            <span class="flight-card__separator">·</span>
            <span class="flight-card__flight-numbers">{{ card.outbound.flightNumbers }}</span>
          }
        </div>
      </div>

      <!-- Multi-city itinerary: stack each leg -->
      @if (card.legs && card.legs.length > 1) {
        @for (leg of card.legs.slice(1); track $index) {
          <div class="flight-card__leg-divider"></div>
          <div class="flight-card__leg">
            <div class="flight-card__leg-head">
              <span class="flight-card__date">{{ leg.departDate }}</span>
            </div>
            <button
              type="button"
              class="flight-card__times"
              [disabled]="isBlocked"
              (click)="onSelectFare($event)"
              i18n-aria-label="@@chatFlightSelectTimesAria"
              aria-label="View this flight on the results page"
            >
              <span class="flight-card__time">{{ leg.departTime }}</span>
              <span class="flight-card__arrow">→</span>
              <span class="flight-card__time">{{ leg.arriveTime }}</span>
            </button>
            <div class="flight-card__route">{{ leg.fromCode }} → {{ leg.toCode }}</div>
            <div class="flight-card__meta">
              @if (leg.duration) {
                <span class="flight-card__duration">{{ leg.duration }}</span>
                <span class="flight-card__separator">·</span>
              }
              <span class="flight-card__stops">{{ stopsLabel(leg.stops) }}</span>
              @if (leg.flightNumbers) {
                <span class="flight-card__separator">·</span>
                <span class="flight-card__flight-numbers">{{ leg.flightNumbers }}</span>
              }
            </div>
          </div>
        }
      } @else if (card.inbound; as inbound) {
        <!-- Inbound leg (return itineraries) -->
        <div class="flight-card__leg-divider"></div>
        <div class="flight-card__leg">
          <div class="flight-card__leg-head">
            <span class="flight-card__date">{{ inbound.departDate }}</span>
          </div>
          <button
            type="button"
            class="flight-card__times"
            [disabled]="isBlocked"
            (click)="onSelectFare($event)"
            i18n-aria-label="@@chatFlightSelectTimesAria"
            aria-label="View this flight on the results page"
          >
            <span class="flight-card__time">{{ inbound.departTime }}</span>
            <span class="flight-card__arrow">→</span>
            <span class="flight-card__time">{{ inbound.arriveTime }}</span>
          </button>
          <div class="flight-card__route">{{ inbound.fromCode }} → {{ inbound.toCode }}</div>
          <div class="flight-card__meta">
            @if (inbound.duration) {
              <span class="flight-card__duration">{{ inbound.duration }}</span>
              <span class="flight-card__separator">·</span>
            }
            <span class="flight-card__stops">{{ stopsLabel(inbound.stops) }}</span>
            @if (inbound.flightNumbers) {
              <span class="flight-card__separator">·</span>
              <span class="flight-card__flight-numbers">{{ inbound.flightNumbers }}</span>
            }
          </div>
        </div>
      }
    </div>

    <div class="flight-card__price-col">
      <span class="flight-card__price-label" i18n="@@chatFlightFromPrice">From</span>
      <span class="flight-card__price">{{ formattedPrice }}</span>
      @if (card.cabin) {
        <span class="flight-card__class-pill">{{ card.cabin }}</span>
      }
      <!-- Policy indicator: same component/tooltip as the results page,
           placed under the headline fare per the travel-policy spec. -->
      <app-warning-tags
        [ticket]="headlineWarningTicket"
        [displayCheck]="false"
        (click)="$event.stopPropagation()"
      ></app-warning-tags>
    </div>
  </div>

  <!-- Fare breakdown panel, shown on expand (mirrors rail card pattern) -->
  @if (isExpanded) {
    <div class="flight-card__fares" (click)="$event.stopPropagation()">
      <div class="flight-card__fares-title" i18n="@@chatFlightAvailableFares">Available fares</div>
      @if (hasFareOptions) {
        <ul class="flight-card__ticket-list">
          @for (ticket of card.fares?.tickets; track $index) {
            <li class="flight-card__ticket">
              <span class="flight-card__ticket-type">{{ ticket.type }}</span>
              <app-warning-tags
                [ticket]="warningTicketFor(ticket)"
                [displayCheck]="false"
                (click)="$event.stopPropagation()"
              ></app-warning-tags>
              <div class="flight-card__ticket-actions">
                <button
                  type="button"
                  class="flight-card__fare-rules"
                  (click)="onFareRules(ticket, $event)"
                  i18n="@@chatFlightFareRules"
                >
                  Fare Rules
                </button>
              </div>
              <button
                type="button"
                class="flight-card__ticket-price"
                [disabled]="ticket.unavailable"
                [attr.aria-disabled]="ticket.unavailable ? true : null"
                (click)="onSelectFareTicket(ticket, $event)"
                i18n-aria-label="@@chatFlightSelectFareAria"
                attr.aria-label="Select {{ ticket.type }} fare, {{
                  formatCurrency(ticket.price, ticket.currency)
                }}"
              >
                {{ formatCurrency(ticket.price, ticket.currency) }}
              </button>
            </li>
          }
        </ul>
      } @else {
        <div class="flight-card__no-fares" i18n="@@chatFlightNoFareOptions">
          No additional fare options available for this flight.
        </div>
      }
    </div>
  }

  <app-chat-card-feedback
    class="flight-card__feedback"
    [feedbackId]="feedbackId"
    [summary]="feedbackSummary"
    (click)="$event.stopPropagation()"
    (keydown)="$event.stopPropagation()"
  ></app-chat-card-feedback>
</div>
